import { createContext, ReactNode, useContext } from "react";
import { useRouter } from "next/router";

import { useCustomVariation } from "@/hooks/useCustomVariation";

/** Hackle 실험 #350 - 장지 상세 비용 허브와 전용 헤더 A/B 테스트. */
export const FACILITY_DETAIL_COST_HUB_EXPERIMENT_KEY = 350;

/** #350의 모수는 개별 장지 상세 방문자만이다. */
const FACILITY_DETAIL_ROUTE = "/facilities/[id]";

/**
 * null 은 "아직 할당 전"이다. A 도 B 도 아니다.
 *
 * 이 페이지는 getStaticPaths + fallback:false 의 완전 SSG 라 빌드 타임 HTML 에
 * 할당이 존재할 수 없고, Hackle 은 클라이언트에서만 팔을 정한다. 그래서 두 팔 중
 * 하나를 정적 HTML 에 박거나, 팔마다 다른 표면을 할당 전까지 비워두거나 둘 중 하나다.
 *
 * 후자를 택했다. 할당 전에 A 를 그려두면 B 할당자가 A 의 CTA 를 잠깐 보고 누를 수 있다.
 * 대신 페이지 전체를 비우지는 않는다. 브레드크럼, 시설 정보, 사용료, 위치, FAQ 같은
 * 공통 콘텐츠는 팔과 무관하므로 그대로 SSR 해야 장지 상세 1,236 페이지의 색인이 유지된다.
 * 실제로 전체를 비웠을 때 body 마크업이 1,762자까지 떨어졌다(정상은 56,899자).
 *
 * 비우는 것은 팔마다 실제로 다른 표면뿐이다. 헤더 메뉴와 비용 CTA 카드.
 */
export type FacilityDetailCostHubArm = "A" | "B" | null;

const FacilityDetailCostHubExperimentContext = createContext<FacilityDetailCostHubArm>(null);

/** 할당된 팔. null 이면 아직 할당 전이라 팔별 표면을 그리면 안 된다. */
export const useFacilityDetailCostHubArm = () => useContext(FacilityDetailCostHubExperimentContext);

/** true면 B안(비용 허브와 장지 전용 헤더)을 노출한다. */
export const useIsFacilityDetailCostHubExperiment = () => useFacilityDetailCostHubArm() === "B";

function AssignedProvider({ children }: { children: ReactNode }) {
  const variation = useCustomVariation(FACILITY_DETAIL_COST_HUB_EXPERIMENT_KEY);
  const arm: FacilityDetailCostHubArm = variation === "A" || variation === "B" ? variation : null;

  return (
    <FacilityDetailCostHubExperimentContext.Provider value={arm}>
      {children}
    </FacilityDetailCostHubExperimentContext.Provider>
  );
}

/**
 * Layout 최상단의 단일 할당 지점.
 * Header와 상세 본문이 같은 Context를 읽으므로 두 표면의 A/B 처치가 어긋나지 않는다.
 * 다른 경로에서는 AssignedProvider가 마운트되지 않아 #350 노출도 기록되지 않는다.
 *
 * 모수 밖에서는 null 이 아니라 "A" 로 둔다. 헤더가 이 값으로 자리를 비울지 정하는데,
 * 장지 상세가 아닌 페이지에서 전역 GNB 가 비면 안 되기 때문이다.
 */
export function FacilityDetailCostHubExperimentProvider({ children }: { children: ReactNode }) {
  const router = useRouter();

  if (router.pathname !== FACILITY_DETAIL_ROUTE) {
    return (
      <FacilityDetailCostHubExperimentContext.Provider value="A">
        {children}
      </FacilityDetailCostHubExperimentContext.Provider>
    );
  }

  return <AssignedProvider>{children}</AssignedProvider>;
}
